// src/pages/UploadPage.js
//
// UploadPage (Admin utilities)
// ============================
//
// Features:
// - Manual Excel upload
// - Trigger server-side “Booksy fetch + import latest”
// - List Excel backups stored on the server
//
// AUTH POLICY (IMPORTANT)
// -----------------------
// This page is inside ProtectedRoute, so normally it won't render if not authenticated.
// However, a tab can lose auth later (401 responses).
//
// ✅ On 401 we must NOT call handleLogout() (server logout).
//    That would clear cookies and log out all tabs.
// ✅ We call softLogout() (local-only) instead.
//
// UX notes:
// - Long-running flows can take time -> show "Running…"
// - Optionally show backend stdout/stderr in a collapsible panel
// - After Booksy flow we refresh list immediately and for a short time via polling
//
// i18n notes:
// - This page belongs to the visit/import administration area, so its visible
//   frontend-owned strings are stored under the existing "visits" namespace.
// - Backend stdout/stderr and backend-provided script messages are intentionally
//   rendered as payload content. Translating those strings on the frontend would
//   make diagnostics less reliable and could hide the exact server-side output.
// - Native browser file inputs expose labels such as "Choose file" and
//   "No file chosen" from the browser/OS UI, not from React or i18next. To keep
//   the import screen fully localizable, this page uses an accessible custom
//   picker button while keeping the real <input type="file"> as the technical
//   file source behind the scenes.

import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../context/AuthContext";
import { uploadFile, listExcelFiles, runBooksyFlow } from "../services/visitService";
import "./UploadPage.css";

const UploadPage = () => {
  const { t } = useTranslation(["visits", "common"]);
  const { softLogout } = useContext(AuthContext);

  // ---------- State ----------
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [isUploading, setIsUploading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // Backend details (optional)
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState(null);

  // polling control (avoid stacking)
  const pollRef = useRef(null);

  // The hidden native file input is triggered by a localized button.
  // This keeps the selected File object coming from the real browser input,
  // while the visible UI remains fully controlled by our i18n dictionaries.
  const fileInputRef = useRef(null);

  // Polling config after “Run Booksy Flow”
  const POLL_INTERVAL_MS = 15000;  // every 15s
  const POLL_DURATION_MS = 120000; // ~2 minutes

  // ---------- Helpers ----------
  const clearMsgs = () => {
    setMessage("");
    setError("");
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  /**
   * Central 401 handling for this page.
   * We do a "local-only logout" to avoid logging out other tabs.
   */
  const handle401 = () => {
    softLogout("UPLOAD_PAGE_401");
  };

  const formatDateTime = (value) => {
    const locale = t("common:language.locale");
    return new Date(value).toLocaleString(locale);
  };

  const loadFiles = async () => {
    try {
      const list = await listExcelFiles();
      setFiles(Array.isArray(list) ? list : []);
    } catch (err) {
      // If auth is gone, softLogout
      if (err?.response?.status === 401) {
        handle401();
        return;
      }

      console.error("[UploadPage] Failed to list files:", err);
      setError(t("visits:uploadPage.messages.loadFileListFailed"));
    }
  };

  // ---------- Effects ----------
  useEffect(() => {
    // Initial file list on mount
    loadFiles();

    // Cleanup polling on unmount
    return () => {
      stopPolling();
    };

  }, []); // keep simple; loadFiles uses stable service calls

  // ---------- Handlers ----------
  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    clearMsgs();
  };

  const handleOpenFilePicker = () => {
    if (isUploading || isRunning) {
      return;
    }

    fileInputRef.current?.click();
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      setError(t("visits:uploadPage.messages.selectFileFirst"));
      return;
    }

    setIsUploading(true);
    clearMsgs();
    setDetails(null);
    setShowDetails(false);

    try {
      const msg = await uploadFile(file);
      setMessage(msg || t("visits:uploadPage.messages.uploadCompleted"));
      setFile(null);

      // Clearing the native input value is required because the visible file
      // name is now rendered by React state. It also allows selecting the same
      // Excel file again after a successful upload if needed.
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadFiles(); // refresh list
    } catch (err) {
      if (err?.response?.status === 401) {
        handle401();
        return;
      }

      console.error("[UploadPage] Upload failed:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("visits:uploadPage.messages.uploadFailed")
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRunBooksyFlow = async () => {
    // Avoid stacking intervals if user clicks multiple times quickly
    stopPolling();

    setIsRunning(true);
    clearMsgs();
    setDetails(null);

    // Auto-open details panel so user can inspect output
    setShowDetails(true);

    try {
      const result = await runBooksyFlow();

      const totalSec =
        typeof result?.totalDurationMs === "number"
          ? (result.totalDurationMs / 1000).toFixed(1)
          : null;

      const baseMsg = result?.message || t("visits:uploadPage.messages.booksyFlowExecuted");
      setMessage(
        totalSec
          ? t("visits:uploadPage.messages.flowCompletedWithTotal", {
              message: baseMsg,
              seconds: totalSec,
            })
          : baseMsg
      );

      // Keep details for user inspection
      setDetails({
        fetch: result?.fetch,
        importLatest: result?.importLatest,
        totalDurationMs: result?.totalDurationMs,
        startedAt: result?.startedAt,
        finishedAt: result?.finishedAt,
      });

      // Refresh immediately…
      await loadFiles();

      // …and then poll briefly to catch new files that may land slightly later
      const startedAt = Date.now();

      pollRef.current = setInterval(async () => {
        const elapsed = Date.now() - startedAt;

        if (elapsed > POLL_DURATION_MS) {
          stopPolling();
          return;
        }

        await loadFiles();
      }, POLL_INTERVAL_MS);
    } catch (err) {
      if (err?.response?.status === 401) {
        handle401();
        return;
      }

      console.error("[UploadPage] Booksy flow failed:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("visits:uploadPage.messages.booksyFlowFailed")
      );
    } finally {
      setIsRunning(false);
    }
  };

  // ---------- Render ----------
  return (
    <div className="upload-page">
      <h1 className="upload-header">{t("visits:uploadPage.title")}</h1>

      {/* Manual upload */}
      <form onSubmit={handleUpload} className="upload-form" noValidate>
        <label className="file-label" htmlFor="excel-upload">
          {t("visits:uploadPage.manualUpload.selectExcelFile")}
        </label>

        <div className="file-picker">
          <input
            id="excel-upload"
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            className="file-input-native"
            disabled={isUploading || isRunning}
            aria-label={t("visits:uploadPage.manualUpload.fileInputAriaLabel")}
          />

          <button
            type="button"
            className="file-picker-button"
            onClick={handleOpenFilePicker}
            disabled={isUploading || isRunning}
          >
            {t("visits:uploadPage.manualUpload.chooseFile")}
          </button>

          <span className="file-picker-name" aria-live="polite">
            {file?.name || t("visits:uploadPage.manualUpload.noFileChosen")}
          </span>
        </div>

        <button
          type="submit"
          disabled={isUploading || !file}
          className={`btn primary ${isUploading ? "loading" : ""}`}
        >
          {isUploading
            ? t("visits:uploadPage.manualUpload.uploading")
            : t("visits:uploadPage.manualUpload.uploadFile")}
        </button>
      </form>

      {/* Automated flow */}
      <div className="flow-actions">
        <button
          onClick={handleRunBooksyFlow}
          disabled={isRunning}
          className={`btn secondary ${isRunning ? "loading" : ""}`}
        >
          {isRunning
            ? t("visits:uploadPage.booksyFlow.running")
            : t("visits:uploadPage.booksyFlow.run")}
        </button>
      </div>

      {/* Messages */}
      {message && <p className="msg success">{message}</p>}
      {error && <p className="msg error">{error}</p>}

      {/* Optional details (stdout/stderr from backend scripts) */}
      {details && (
        <div className="details">
          <button
            type="button"
            className="details-toggle"
            onClick={() => setShowDetails((s) => !s)}
            aria-expanded={showDetails}
          >
            {showDetails
              ? t("visits:uploadPage.details.hide")
              : t("visits:uploadPage.details.show")}
          </button>

          {showDetails && (
            <div className="details-panel">
              {typeof details.startedAt === "number" && typeof details.finishedAt === "number" && (
                <div className="kv">
                  <div>
                    <strong>{t("visits:uploadPage.details.started")}:</strong>{" "}
                    {formatDateTime(details.startedAt)}
                  </div>
                  <div>
                    <strong>{t("visits:uploadPage.details.finished")}:</strong>{" "}
                    {formatDateTime(details.finishedAt)}
                  </div>
                </div>
              )}

              {details.fetch && (
                <div className="block">
                  <h3>{t("visits:uploadPage.details.fetchScript")}</h3>
                  <div className="kv">
                    <div>
                      <strong>{t("visits:uploadPage.details.exitCode")}:</strong> {details.fetch.code}
                    </div>
                    <div>
                      <strong>{t("visits:uploadPage.details.duration")}:</strong>{" "}
                      {t("visits:uploadPage.details.secondsShort", {
                        seconds: ((details.fetch.durationMs || 0) / 1000).toFixed(1),
                      })}
                    </div>
                  </div>

                  {details.fetch.stdout && (
                    <>
                      <h4>{t("visits:uploadPage.details.stdout")}</h4>
                      <pre className="pre">{details.fetch.stdout}</pre>
                    </>
                  )}

                  {details.fetch.stderr && String(details.fetch.stderr).trim() && (
                    <>
                      <h4>{t("visits:uploadPage.details.stderr")}</h4>
                      <pre className="pre pre-error">{details.fetch.stderr}</pre>
                    </>
                  )}
                </div>
              )}

              {details.importLatest && (
                <div className="block">
                  <h3>{t("visits:uploadPage.details.importScript")}</h3>
                  <div className="kv">
                    <div>
                      <strong>{t("visits:uploadPage.details.exitCode")}:</strong> {details.importLatest.code}
                    </div>
                    <div>
                      <strong>{t("visits:uploadPage.details.duration")}:</strong>{" "}
                      {t("visits:uploadPage.details.secondsShort", {
                        seconds: ((details.importLatest.durationMs || 0) / 1000).toFixed(1),
                      })}
                    </div>
                  </div>

                  {details.importLatest.stdout && (
                    <>
                      <h4>{t("visits:uploadPage.details.stdout")}</h4>
                      <pre className="pre">{details.importLatest.stdout}</pre>
                    </>
                  )}

                  {details.importLatest.stderr && String(details.importLatest.stderr).trim() && (
                    <>
                      <h4>{t("visits:uploadPage.details.stderr")}</h4>
                      <pre className="pre pre-error">{details.importLatest.stderr}</pre>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* File list */}
      <div className="file-list">
        <h2>{t("visits:uploadPage.fileList.title")}</h2>
        {files.length === 0 ? (
          <p className="muted">{t("visits:uploadPage.fileList.empty")}</p>
        ) : (
          <ul className="file-ul">
            {files.map((f) => (
              <li key={`${f.name}-${f.mtime}`} className="file-li">
                <span className="file-name">{f.name}</span>
                <span className="file-meta">
                  {t("visits:uploadPage.fileList.fileMeta", {
                    size: f.size,
                    date: formatDateTime(f.mtime),
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UploadPage;
